import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuthStore from '../../store/authStore';
import {
  LayoutDashboard, Warehouse, Package, Boxes, Factory, ShoppingCart, FileText, ArrowLeftRight, BarChart3, Users, Shield, LogOut, Menu, X, Search, Archive, Truck, Landmark, MessageCircle, Ship, Wrench, Calculator, Wallet, Table2, HandCoins, Key, Contact, FileSpreadsheet,
} from 'lucide-react';
import toast from 'react-hot-toast';
import useChatStore from '../../store/chatStore';
import { rawMaterialAPI, authAPI } from '../../utils/api';
import SectionFooter from '../common/SectionFooter';
import NotificationBell from './NotificationBell';
import GlobalSearch from './GlobalSearch';
import PasswordModal from '../common/PasswordModal';

export default function Layout() {
  const { t, i18n } = useTranslation();
  const { user, logout, canRoute, setUser } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  // ---- chat: one poll loop for the whole app (badge, notifications, and the chat page itself)
  const chatUnread = useChatStore(s => s.unreadTotal);
  const onChatPage = location.pathname.startsWith('/chat');
  const pollRef = React.useRef(null);
  useEffect(() => {
    // single loop; kick() restarts the countdown (used when the chat page opens or the tab comes back)
    const ctl = { stop: false, timer: null, busy: false };
    const schedule = (ms) => { clearTimeout(ctl.timer); ctl.timer = setTimeout(tick, ms); };
    async function tick() {
      if (ctl.stop || ctl.busy) return;
      ctl.busy = true;
      let ok = true;
      try { await useChatStore.getState().poll(); } catch (e) { ok = false; }
      ctl.busy = false;
      if (ctl.stop) return;
      const delay = document.hidden ? 30000 : useChatStore.getState().onChatPage ? 2500 : 10000;
      schedule(ok ? delay : Math.max(delay, 15000));
    }
    ctl.kick = () => { if (!ctl.stop && !ctl.busy) schedule(150); };
    pollRef.current = ctl;
    useChatStore.getState().loadConversations().catch(() => {}).finally(tick);
    const wake = () => { if (!document.hidden) ctl.kick(); };
    document.addEventListener('visibilitychange', wake);
    return () => { ctl.stop = true; clearTimeout(ctl.timer); document.removeEventListener('visibilitychange', wake); };
  }, []);
  useEffect(() => { if (onChatPage && pollRef.current) pollRef.current.kick(); }, [onChatPage]);
  // new-message notifications: toast inside the app, system notification when the tab is in the background
  useEffect(() => useChatStore.getState().subscribe((fresh) => {
    const st = useChatStore.getState();
    fresh.filter(m => m.sender_id !== user?.id && !m.deleted).forEach(m => {
      const viewing = st.onChatPage && st.activeId === m.conversation_id && !document.hidden;
      if (viewing) return;
      const conv = st.conversations.find(c => c.id === m.conversation_id);
      const title = conv?.is_group ? `${m.sender_name} · ${conv.title}` : m.sender_name;
      const text = m.kind === 'voice' ? '🎤 ' + t('voice_message') : m.kind === 'image' ? '🖼 ' + (m.body || t('photo'))
        : m.kind === 'file' ? '📎 ' + (m.file_name || t('file')) : (m.body || '');
      if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          const n = new Notification(title, { body: text.slice(0, 140), tag: 'chat-' + m.conversation_id, icon: '/favicon.ico' });
          n.onclick = () => { window.focus(); navigate('/chat?c=' + m.conversation_id); n.close(); };
        } catch (e) { /* some mobile browsers only allow notifications from a service worker */ }
      }
      if (!st.onChatPage || st.activeId !== m.conversation_id) {
        toast((tt) => (
          <button className="chat-toast" onClick={() => { toast.dismiss(tt.id); navigate('/chat?c=' + m.conversation_id); }}>
            <MessageCircle size={16} />
            <span><b>{title}</b><small dir="auto">{text.slice(0, 90)}</small></span>
          </button>
        ), { id: 'chat-' + m.conversation_id, duration: 5000 });
      }
    });
  }), [user?.id, t]);
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\)\s*/, '');
    document.title = chatUnread > 0 ? `(${chatUnread > 99 ? '99+' : chatUnread}) ${base}` : base;
  }, [chatUnread]);

  useEffect(() => {
    rawMaterialAPI.lowStock().then(r => setLowStockCount(r.data.length)).catch(() => {});
  }, []);

  useEffect(() => {
    const el = document.querySelector('.main-content');
    if (el) el.dir = i18n.language === 'fa' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // Sidebar & shell always LTR; only the content area flips to RTL for Persian
  useEffect(() => { document.documentElement.dir = 'ltr'; document.documentElement.lang = i18n.language; }, [i18n.language]);

  const handleLogout = async () => { try { await authAPI.logout(); } catch (e) { /* ignore */ } useChatStore.getState().reset(); logout(); navigate('/login'); };
  // sessions started before v3.6 have no file cookie yet: without it /uploads (images, voice, invoices) answers 401
  useEffect(() => {
    let fresh = false;
    try { fresh = sessionStorage.getItem('fresh_login') === '1'; sessionStorage.removeItem('fresh_login'); } catch (e) { /* ignore */ }
    if (!fresh) authAPI.sessionCookie().catch(() => {});
    // v3.9: role / permissions may have been changed by the admin since this session started
    const refreshMe = () => authAPI.me().then(r => setUser(r.data)).catch(() => {});
    refreshMe();
    const h = setInterval(refreshMe, 300000);
    return () => clearInterval(h);
  }, []); // eslint-disable-line
  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
    const el = document.querySelector('.main-content');
    if (el) el.dir = lang === 'fa' ? 'rtl' : 'ltr';
  };

  const navGroups = [
    {
      label: i18n.language === 'fa' ? 'اصلی' : i18n.language === 'ru' ? 'Главное' : 'Main',
      items: [
        { path: '/', icon: LayoutDashboard, label: t('dashboard') },
        { path: '/chat', icon: MessageCircle, label: t('chat'), badge: chatUnread > 0 ? (chatUnread > 99 ? '99+' : chatUnread) : null, badgeColor: '#14b8a6' },
        { path: '/warehouses', icon: Warehouse, label: t('warehouses') },
        { path: '/raw-materials', icon: Package, label: t('raw_materials'), badge: lowStockCount > 0 ? lowStockCount : null },
        { path: '/finished-goods', icon: Boxes, label: t('finished_goods') },
        { path: '/inventory', icon: Archive, label: t('inventory') },
        { path: '/misc-goods', icon: Wrench, label: t('misc_goods') },
      ]
    },
    {
      label: i18n.language === 'fa' ? 'عملیات' : i18n.language === 'ru' ? 'Операции' : 'Operations',
      items: [
        { path: '/production', icon: Factory, label: t('production') },
        { path: '/purchases', icon: Truck, label: t('purchases') },
        { path: '/shipments', icon: Ship, label: t('shipments') },
        { path: '/sales', icon: ShoppingCart, label: t('sales') },
        { path: '/lading-bills', icon: FileText, label: t('lading_bills') },
        { path: '/transfers', icon: ArrowLeftRight, label: t('transfers') },
      ]
    },
    {
      label: i18n.language === 'fa' ? 'مدیریت' : i18n.language === 'ru' ? 'Управление' : 'Management',
      items: [
        { path: '/accounting', icon: Landmark, label: t('accounting') },
        { path: '/expenses', icon: Wallet, label: t('expenses') },
        { path: '/prepayments', icon: HandCoins, label: t('prepayments') },
        { path: '/payroll', icon: Contact, label: t('payroll') },
        { path: '/costing', icon: Calculator, label: t('costing') },
        { path: '/reports', icon: BarChart3, label: t('reports') },
        { path: '/report-builder', icon: Table2, label: t('report_builder') },
        { path: '/import', icon: FileSpreadsheet, label: t('import_data') },
        { path: '/users', icon: Users, label: t('users') },
        { path: '/audit-log', icon: Shield, label: t('audit_log') },
      ]
    }
  ].map(g => ({ ...g, items: g.items.filter(n => canRoute(n.path)) })).filter(g => g.items.length);   // v3.9: only pages the user may open

  const allNavItems = navGroups.flatMap(g => g.items);
  // mobile bottom bar: five shortcuts (chat replaced finished goods in v3.3); everything else is under "More"
  const bottomNavItems = ['/', '/chat', '/warehouses', '/raw-materials', '/production'].map(p => allNavItems.find(n => n.path === p)).filter(Boolean);
  const roleColors = { admin: '#ef4444', manager: '#6366f1', sales_manager: '#10b981', production_manager: '#f59e0b' };
  const userColor = roleColors[user?.role] || '#6366f1';
  const currentPage = allNavItems.find(n => n.path === location.pathname);
  const lang = i18n.language;

  return (
    <div className="app-shell" style={{
      display: 'flex', overflow: 'hidden',
      background: '#070b14',
      position: 'relative',
    }}>
      {/* Background gradient blobs */}
      <div style={{
        position: 'fixed', top: '-5%', left: '5%',
        width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.09) 0%, transparent 70%)',
        pointerEvents: 'none', zIndex: 0,
      }} />
      <div style={{
        position: 'fixed', bottom: '-10%', right: '10%',
        width: 600, height: 600, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(67,56,202,0.07) 0%, transparent 70%)',
        pointerEvents: 'none', zIndex: 0,
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '40%',
        width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(16,185,129,0.05) 0%, transparent 70%)',
        pointerEvents: 'none', zIndex: 0,
      }} />

      {/* SIDEBAR */}
      <aside className="desktop-sidebar" style={{
        width: sidebarOpen ? 236 : 60,
        flexShrink: 0,
        display: 'flex', flexDirection: 'column',
        background: '#080c18',
        borderRight: '1px solid rgba(99,102,241,0.15)',
        transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
        overflow: 'hidden',
        position: 'relative', zIndex: 10,
      }}>
        {/* Vertical glow line on border */}
        <div style={{
          position: 'absolute', top: 0, bottom: 0, right: 0, width: 1,
          background: 'linear-gradient(180deg, transparent 0%, rgba(99,102,241,0.5) 30%, rgba(139,92,246,0.3) 60%, transparent 100%)',
          pointerEvents: 'none',
        }} />

        {/* Logo */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '0 12px', height: 62,
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 11,
            background: 'linear-gradient(135deg, #6366f1, #4338ca)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 4px 20px rgba(99,102,241,0.55)',
          }}>
            <Factory size={18} color="white" />
          </div>
          {sidebarOpen && (
            <div style={{ overflow: 'hidden' }}>
              <div style={{
                fontWeight: 800, fontSize: 14, color: '#fff',
                whiteSpace: 'nowrap', letterSpacing: '-0.2px',
              }}>{t('app_name')}</div>
              <div style={{
                fontSize: 9, color: 'rgba(99,102,241,0.7)',
                fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em',
              }}>ERP System</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav style={{
          flex: 1, padding: '12px 8px',
          overflowY: 'auto', overflowX: 'hidden',
        }}>
          {navGroups.map((group, gi) => (
            <div key={gi} style={{ marginBottom: 20 }}>
              {sidebarOpen && (
                <div style={{
                  fontSize: 9, fontWeight: 800,
                  color: 'rgba(99,102,241,0.4)',
                  textTransform: 'uppercase', letterSpacing: '0.12em',
                  padding: '0 10px', marginBottom: 6,
                }}>
                  {group.label}
                </div>
              )}
              {group.items.map(item => {
                const active = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link key={item.path} to={item.path} style={{
                    display: 'flex', alignItems: 'center',
                    gap: 10,
                    padding: sidebarOpen ? '9px 10px' : '10px 0',
                    justifyContent: sidebarOpen ? 'flex-start' : 'center',
                    borderRadius: 11, marginBottom: 2,
                    textDecoration: 'none',
                    background: active
                      ? 'linear-gradient(90deg, rgba(99,102,241,0.2), rgba(99,102,241,0.05))'
                      : 'transparent',
                    color: active ? '#a5b4fc' : 'rgba(255,255,255,0.3)',
                    borderLeft: active ? '4px solid #6366f1' : '4px solid transparent',
                    transition: 'all 0.15s',
                    fontWeight: active ? 600 : 500, fontSize: 13,
                    boxShadow: active ? '0 2px 16px rgba(99,102,241,0.18)' : 'none',
                    paddingLeft: sidebarOpen ? (active ? '8px' : '10px') : undefined,
                  }}
                    onMouseEnter={e => {
                      if (!active) {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                        e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!active) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = 'rgba(255,255,255,0.3)';
                      }
                    }}
                  >
                    <Icon size={16} style={{ flexShrink: 0 }} />
                    {sidebarOpen && <span style={{ whiteSpace: 'nowrap', flex: 1 }}>{item.label}</span>}
                    {!sidebarOpen && item.badge && <span className="nav-dot" style={{ background: item.badgeColor || '#ef4444' }} />}
                    {sidebarOpen && item.badge && (
                      <span style={{
                        background: item.badgeColor || '#ef4444', color: '#fff',
                        fontSize: 9, fontWeight: 800,
                        padding: '2px 7px', borderRadius: 99, lineHeight: 1.4,
                      }}>{item.badge}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User section */}
        <div style={{ padding: '8px 8px 14px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          {sidebarOpen ? (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 9,
                padding: '9px 10px', borderRadius: 11,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.06)',
                marginBottom: 6,
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                  background: `linear-gradient(135deg, ${userColor}, ${userColor}88)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 800, color: '#fff',
                  boxShadow: `0 4px 14px ${userColor}45`,
                }}>
                  {user?.full_name?.[0]?.toUpperCase()}
                </div>
                <div style={{ overflow: 'hidden', flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.full_name}</div>
                  <div style={{ fontSize: 9, color: userColor, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('role_' + user?.role)}</div>
                </div>
              </div>
              <button className="lay-pwd" onClick={() => setPwdOpen(true)}><Key size={14} /> {t('my_password')}</button>
              <button
                onClick={handleLogout}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px', borderRadius: 10, width: '100%',
                  background: 'none', border: '1px solid transparent',
                  cursor: 'pointer', color: 'rgba(255,255,255,0.2)',
                  fontSize: 12, fontWeight: 500, fontFamily: 'inherit', transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.09)'; e.currentTarget.style.color = '#f87171'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'rgba(255,255,255,0.2)'; e.currentTarget.style.borderColor = 'transparent'; }}
              >
                <LogOut size={14} /> {t('logout')}
              </button>
            </>
          ) : (
            <button
              onClick={handleLogout}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 8, borderRadius: 10, width: '100%',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'rgba(255,255,255,0.2)', fontFamily: 'inherit', transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}
            >
              <LogOut size={16} />
            </button>
          )}
        </div>
      </aside>

      {/* MAIN */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, position: 'relative' }}>

        {/* HEADER */}
        <header style={{
          height: 60, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 20px', gap: 14,
          background: 'rgba(7,11,20,0.85)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          backdropFilter: 'blur(20px)',
          position: 'sticky', top: 0, zIndex: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="desktop-sidebar"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center',
                padding: 7, borderRadius: 9, transition: 'all 0.15s', fontFamily: 'inherit',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.25)'; e.currentTarget.style.background = 'none'; }}
            >
              <Menu size={18} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <span style={{ color: 'rgba(255,255,255,0.18)', fontSize: 16 }}>/</span>
              <span style={{ color: '#fff', fontWeight: 700 }}>{currentPage?.label || t('dashboard')}</span>
            </div>
          </div>

          {/* Search (v4.1.2: real search; it was only a decoration before) */}
          <GlobalSearch pages={allNavItems} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Language pills */}
            <div style={{
              display: 'flex', gap: 2,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 10, padding: 3,
            }}>
              {['en', 'ru', 'fa'].map(l => (
                <button
                  key={l}
                  onClick={() => changeLanguage(l)}
                  style={{
                    padding: '4px 10px', borderRadius: 7,
                    fontSize: 10, fontWeight: 800, cursor: 'pointer',
                    border: 'none', fontFamily: 'inherit', transition: 'all 0.15s',
                    background: lang === l ? '#6366f1' : 'transparent',
                    color: lang === l ? '#fff' : 'rgba(255,255,255,0.28)',
                    boxShadow: lang === l ? '0 2px 10px rgba(99,102,241,0.5)' : 'none',
                  }}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>

            <GlobalSearch pages={allNavItems} mode="mobile" />
            {/* Notifications: unread chat + low stock */}
            <NotificationBell />

            {/* User chip */}
            <div className="desktop-sidebar" style={{
              display: 'flex', alignItems: 'center', gap: 9,
              padding: '6px 12px 6px 7px', borderRadius: 11,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 9,
                background: `linear-gradient(135deg, ${userColor}, ${userColor}88)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 800, color: '#fff', flexShrink: 0,
              }}>
                {user?.full_name?.[0]?.toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{user?.full_name}</div>
                <div style={{ fontSize: 9, color: userColor, fontWeight: 700, textTransform: 'uppercase' }}>{t('role_' + user?.role)}</div>
              </div>
            </div>

            <button
              onClick={() => setMobileMenuOpen(true)}
              style={{
                display: 'none', background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',
                cursor: 'pointer', color: 'rgba(255,255,255,0.6)',
                padding: 8, borderRadius: 9, fontFamily: 'inherit',
              }}
              className="mobile-menu-btn"
            >
              <Menu size={19} />
            </button>
          </div>
        </header>

        {/* Mobile overlay */}
        {mobileMenuOpen && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 200, backdropFilter: 'blur(12px)' }}
            onClick={() => setMobileMenuOpen(false)}
          >
            <div
              style={{
                position: 'absolute', top: 0, right: 0,
                width: '82%', maxWidth: 300, height: '100%',
                background: '#080c18',
                borderLeft: '1px solid rgba(99,102,241,0.15)',
                display: 'flex', flexDirection: 'column',
                padding: '16px 10px',
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 16, padding: '0 6px 14px',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 10,
                    background: 'linear-gradient(135deg,#6366f1,#4338ca)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 16px rgba(99,102,241,0.45)',
                  }}>
                    <Factory size={16} color="white" />
                  </div>
                  <span style={{ fontWeight: 800, fontSize: 14, color: '#fff' }}>{t('app_name')}</span>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontFamily: 'inherit' }}
                ><X size={18} /></button>
              </div>
              <nav style={{ flex: 1, overflowY: 'auto' }}>
                {navGroups.map((group, gi) => (
                  <div key={gi} style={{ marginBottom: 18 }}>
                    <div style={{
                      fontSize: 9, fontWeight: 800, color: 'rgba(99,102,241,0.45)',
                      textTransform: 'uppercase', letterSpacing: '0.12em',
                      padding: '0 10px', marginBottom: 6,
                    }}>{group.label}</div>
                    {group.items.map(item => {
                      const active = location.pathname === item.path;
                      const Icon = item.icon;
                      return (
                        <Link key={item.path} to={item.path} style={{
                          display: 'flex', alignItems: 'center', gap: 11,
                          padding: '11px 12px', borderRadius: 11, marginBottom: 2,
                          textDecoration: 'none',
                          background: active ? 'linear-gradient(90deg,rgba(99,102,241,0.2),rgba(99,102,241,0.08))' : 'transparent',
                          color: active ? '#a5b4fc' : 'rgba(255,255,255,0.45)',
                          fontSize: 13, fontWeight: active ? 600 : 500,
                          borderLeft: active ? '4px solid #6366f1' : '4px solid transparent',
                        }}>
                          <Icon size={17} /> <span style={{ flex: 1 }}>{item.label}</span>
                          {item.badge && <span className="drawer-badge" style={{ background: item.badgeColor || '#ef4444' }}>{item.badge}</span>}
                        </Link>
                      );
                    })}
                  </div>
                ))}
              </nav>
              <button className="lay-pwd mob" onClick={() => { setMobileMenuOpen(false); setPwdOpen(true); }}><Key size={16} /> {t('my_password')}</button>
              <button
                onClick={handleLogout}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '11px 12px', borderRadius: 11,
                  background: 'rgba(239,68,68,0.09)', border: '1px solid rgba(239,68,68,0.18)',
                  color: '#f87171', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  marginTop: 8, fontFamily: 'inherit',
                }}
              >
                <LogOut size={16} /> {t('logout')}
              </button>
            </div>
          </div>
        )}

        {/* CONTENT */}
        <main className={`main-content ${onChatPage ? 'is-chat' : ''}`} dir={i18n.language === 'fa' ? 'rtl' : 'ltr'} style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          <div key={location.pathname} className="route-view"><Outlet /></div>
          {!onChatPage && <SectionFooter pathname={location.pathname} />}
        </main>
      </div>

      {/* MOBILE BOTTOM NAV */}
      <nav className="mobile-nav">
        <div className="mobile-nav-items">
          {bottomNavItems.map(item => {
            const active = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link key={item.path} to={item.path} className={`mobile-nav-item ${active ? 'active' : ''}`}>
                <span className="mobile-nav-ico"><Icon size={21} />{item.badge && <em style={{ background: item.badgeColor || '#ef4444' }}>{item.badge}</em>}</span>
                <span>{item.label}</span>
              </Link>
            );
          })}
          <button onClick={() => setMobileMenuOpen(true)} className="mobile-nav-item">
            <Menu size={21} />
            <span>{t('more')}</span>
          </button>
        </div>
      </nav>
      {pwdOpen && <PasswordModal onClose={() => setPwdOpen(false)} />}
    </div>
  );
}
